import { HttpEventType } from '@angular/common/http';
import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ReceiptsService } from 'src/app/services/receipts.service';

type UploadState = 'queued' | 'uploading' | 'done' | 'error';

interface UploadItem {
  file: File;
  state: UploadState;
  error?: string;
}

@Component({
  selector: 'app-receipt-upload-dialog',
  templateUrl: './receipt-upload.dialog.html',
  styleUrls: ['./receipt-upload.dialog.scss']
})
export class ReceiptUploadDialogComponent {
  files: UploadItem[] = [];
  uploading = false;

  constructor(
    private dialogRef: MatDialogRef<ReceiptUploadDialogComponent>,
    private receiptsService: ReceiptsService
  ) {}

  onSelect(event: { addedFiles?: File[] }) {
    const addedFiles = event?.addedFiles || [];
    let addedAny = false;

    for (const file of addedFiles) {
      const exists = this.files.some(item =>
        item.file.name === file.name &&
        item.file.size === file.size &&
        item.file.lastModified === file.lastModified
      );

      if (!exists) {
        this.files.push({ file, state: 'queued' });
        addedAny = true;
      }
    }

    if (addedAny) {
      this.startUpload();
    }
  }

  removeFile(item: UploadItem) {
    if (item.state === 'uploading') {
      return;
    }

    this.files = this.files.filter(file => file !== item);
  }

  close() {
    this.dialogRef.close(this.completedCount > 0);
  }

  retryFailed() {
    for (const item of this.files) {
      if (item.state === 'error') {
        item.state = 'queued';
        item.error = undefined;
      }
    }

    this.startUpload();
  }

  get completedCount(): number {
    return this.files.filter(item => item.state === 'done').length;
  }

  get failedCount(): number {
    return this.files.filter(item => item.state === 'error').length;
  }

  get queuedCount(): number {
    return this.files.filter(item => item.state === 'queued').length;
  }

  private startUpload() {
    if (this.uploading) {
      return;
    }

    const nextItem = this.files.find(item => item.state === 'queued');
    if (!nextItem) {
      return;
    }

    this.uploading = true;
    nextItem.state = 'uploading';

    this.receiptsService.uploadReceipt([nextItem.file], '').subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          nextItem.state = 'done';
          this.uploading = false;
          this.startUpload();
        }
      },
      error: (error) => {
        nextItem.state = 'error';
        nextItem.error = this.getErrorMessage(error);
        this.uploading = false;
        this.startUpload();
      }
    });
  }

  private getErrorMessage(error: any): string {
    const serverError = error?.error?.error ?? error?.error?.message ?? error?.message;

    if (typeof serverError === 'string' && serverError.trim().length > 0) {
      return serverError;
    }

    if (serverError && typeof serverError === 'object') {
      const nested = serverError.message || serverError.error;
      if (typeof nested === 'string' && nested.trim().length > 0) {
        return nested;
      }
    }

    return 'Upload failed';
  }
}
